import React from 'react';
import styles from './styles.module.scss';
import { Link, LinkProps } from 'react-router-dom';

type ButtonVariant = 'primary' | 'outline' | 'text' | 'whiteOutline'| 'goldenOutline';
type ButtonSize = 'sm' | 'md' | 'lg';

interface BaseProps {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string; // custom class option
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => void;
}

interface AsButtonProps extends BaseProps {
  as?: 'button';
  to?: never;
}

interface AsLinkProps extends BaseProps {
  as?: 'link';
  to: LinkProps['to'];
  type?: never;
}

export type AppButtonProps = AsButtonProps | AsLinkProps;

const cx = (...classes: (string | false | null | undefined)[]) => classes.filter(Boolean).join(' ');

export const Button: React.FC<AppButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth,
  className,
  loading = false,
  disabled,
  type = 'button',
  as = 'button',
  onClick,
  ...rest
}) => {
  const classes = cx(
    styles.buttonBase,
    styles[variant],
    size !== 'md' ? styles[`size-${size}`] : '',
    fullWidth ? styles.fullWidth : '',
    className
  );

  const content = (
    <>
      {loading && <span className={styles.loadingSpinner} aria-hidden="true" />}
      <span>{children}</span>
    </>
  );

  if (as === 'link') {
    const { to } = rest as AsLinkProps;
    return (
      <Link to={to} className={classes} onClick={onClick} aria-disabled={disabled || loading}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {content}
    </button>
  );
};

export default Button;
